import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import { Condition } from 'Component/others';
import ProgressHeader from 'Component/progress-header';
import { useAppSystem } from 'Store/app';
import useGetPresences from 'Domain/host-attendance-record/useGetPresences';
import LivestreamBanner from 'View/host-stream/components/livestream-banner';
import StreamRecord from 'View/host-stream/section/stream-record';
import StreamForm from 'View/host-stream/section/stream-form';
import StreamUpdateGiveaway from 'View/host-stream/section/stream-update-giveaway';
import StreamControl from 'View/host-stream/section/stream-control';
import HostStreamProvider, { useHostStreamState } from 'View/host-stream/state';
import useBackModalRestriction from 'View/host-stream/hooks/useBackModalRestriction';

import styles from './styles.module.css';

const HostStream = () => {
  const navigate = useNavigate();
  const { state, updateState } = useHostStreamState();
  const { onChangeAppSystem } = useAppSystem();
  const { onBackRestriction } = useBackModalRestriction();

  const { data: presenceData } = useGetPresences();

  const { isLivestreaming, lastSession, isClockedIn, streamingSessions } = presenceData ?? {};
  const isInitialized = useRef(false);

  const isPreviewStep = (!isLivestreaming && state?.currentStep === 2) || (isLivestreaming && state?.currentStep === 3);

  useEffect(() => {
    if (!isInitialized.current) return;
    if (state?.selectedView === 'control') {
      onChangeAppSystem({ isHideHelpButton: false });
    } else {
      onChangeAppSystem({ isHideHelpButton: true });
    }
  }, [state?.selectedView, onChangeAppSystem]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      onChangeAppSystem({ isHideHelpButton: false });
    };
  }, [onChangeAppSystem]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLivestreaming) {
      updateState({ selectedView: 'record', currentStep: 0 });
    }
    isInitialized.current = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBack = () => {
    if (state?.currentStep === 0) isLivestreaming ? updateState({ selectedView: 'control' }) : navigate(-1);
    if (state?.currentStep === 1) updateState({ currentStep: 0, capturedImage: { first: null, second: null }, tempCapturedImage: null });
    if (state?.currentStep === 2) updateState({ currentStep: 0, capturedImage: { first: null, second: null }, tempCapturedImage: null, notes: '' });
    if (state?.currentStep === 3) updateState({ currentStep: 2 });
  };

  return (
    <Box className={styles['host-stream__container']}>
      <Condition statement={state?.selectedView === 'record'}>
        <ProgressHeader
          variant={isLivestreaming ? 'endStream' : 'default'}
          currentStep={state?.currentStep}
          onBackHandler={isPreviewStep ? () => onBackRestriction({ submit: handleBack }) : handleBack}
        />
        <LivestreamBanner isLivestreaming={isLivestreaming} streamNumber={streamingSessions?.length} />
        <Condition statement={state?.currentStep < 2}>
          <StreamRecord />
        </Condition>
        <Condition statement={isLivestreaming && state?.currentStep === 2}>
          <StreamUpdateGiveaway />
        </Condition>
        <Condition statement={isPreviewStep}>
          <StreamForm isClockedIn={isClockedIn} isLivestreaming={isLivestreaming} streamNumber={streamingSessions?.length} />
        </Condition>
      </Condition>
      <Condition statement={state?.selectedView === 'control'}>
        <StreamControl startTime={lastSession?.clockIn?.time} note={lastSession?.clockIn?.note} streamNumber={streamingSessions?.length} />
      </Condition>
    </Box>
  );
};

const HostStreamWrapper = () => <HostStreamProvider children={<HostStream />} />;

export default HostStreamWrapper;
